import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAbly } from './ablyContext';
import { ButtonState, SITE_SERVICE } from '../common/constants';
import { primaryAuth } from '../firebase';

/**
 * Custom hook to control a door and track its live state machine.
 *
 * @param {object} params
 * @param {string} params.doorId The door's unique identifier
 * @param {string} params.siteId The site's unique identifier
 */
export default function DoorControl({ doorId, siteId }) {
  const [countdown, setCountdown] = useState(0);
  const [doorState, setDoorState] = useState('UNAVAILABLE');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const ably = useAbly(); // single shared connection instance
  const timerRef = useRef(null);

  /**
   * LIVE PRESENCE LISTENER (Ably client-side)
   */
  useEffect(() => {
    if (!ably || !doorId || !siteId) return undefined;
    // HMR guard: do not attempt to subscribe if client is closing or closed
    if (['closed', 'closing', 'failed'].includes(ably.connection.state)) {
      return undefined;
    }

    // reuse the single existing WebSocket to attach to this door's channel
    const channelName = `site:${siteId}:door:${doorId}`;
    const channel = ably.channels.get(channelName);

    const updatePresence = async () => {
      try {
        const members = await channel.presence.get();

        if (members.length === 1) {
          // keep counting down if already active, otherwise READY
          setDoorState((prev) =>
            prev === ButtonState.IN_USE
              ? ButtonState.IN_USE
              : ButtonState.READY,
          );
        } else if (members.length === 0) {
          // 0 members: OFFLINE
          setDoorState(ButtonState.OFFLINE);
          clearInterval(timerRef.current);
          setCountdown(0);
        } else {
          // >1 members: CONFLICT
          setDoorState(ButtonState.CONFLICT);
          clearInterval(timerRef.current);
          setCountdown(0);
        }
      } catch {
        setDoorState(ButtonState.OFFLINE);
      }
    };

    updatePresence(); // check on mount

    // subscribe to live edge events (agent boots or drops)
    channel.presence.subscribe(['enter', 'leave'], updatePresence);

    return () => {
      // only unsubscribe if the connection is still alive
      if (!['closed', 'closing'].includes(ably.connection.state)) {
        channel.presence.unsubscribe(['enter', 'leave'], updatePresence);
      }
      clearInterval(timerRef.current);
    };
  }, [ably, doorId, siteId]);

  /**
   * COUNTDOWN TIMER (background tick)
   */
  const startCountdown = useCallback((seconds) => {
    clearInterval(timerRef.current);
    const duration = Math.ceil(seconds);
    setCountdown(duration);
    setDoorState(ButtonState.IN_USE);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setDoorState(ButtonState.READY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /**
   * UNLOCK ACTION (post to backend)
   * @returns
   */
  const unlock = async () => {
    if (doorState === ButtonState.UNAVAILABLE || loading) return;
    // guard against unauthenticated state
    if (!primaryAuth.currentUser) {
      setErrorMsg('User session expired. Please log in.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      // extract token from firebase JWT
      const idToken = await primaryAuth.currentUser.getIdToken();
      const response = await axios.post(
        `${SITE_SERVICE}/doors/unlock`,
        { doorId, siteId },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      const duration = response.data?.lockDurationSec || 7; // fallback duration
      startCountdown(duration);
    } catch (error) {
      const newState =
        error.response?.status === 409
          ? ButtonState.CONFLICT
          : ButtonState.OFFLINE; // 504 Timeout or network drop
      setDoorState(newState);
      clearInterval(timerRef.current);
      setCountdown(0);
      // use message from door agent (with generic fallback)
      setErrorMsg(error.response?.data?.message ?? 'Door is unresponsive');
    } finally {
      setLoading(false);
    }
  };

  return {
    countdown,
    doorState,
    errorMsg,
    loading,
    unlock,
  };
}
